import { useState, useEffect } from 'react'
import Api from 'services/api'

// Module-level shared state so all hook instances stay in sync
let sharedStatus = false
let sharedHasLoadedTermsStatus = false
let sharedRevision: string | undefined
const statusListeners = new Set<(v: boolean) => void>()
const hasLoadedListeners = new Set<(v: boolean) => void>()
const revisionListeners = new Set<(v: string | undefined) => void>()

const setSharedStatus = (value: boolean) => {
  sharedStatus = value
  statusListeners.forEach(fn => fn(value))
}

const setSharedHasLoadedTermsStatus = (value: boolean) => {
  sharedHasLoadedTermsStatus = value
  hasLoadedListeners.forEach(fn => fn(value))
}

const setSharedRevision = (value: string | undefined) => {
  sharedRevision = value
  revisionListeners.forEach(fn => fn(value))
}

const useChatbotTermsApi = () => {
  const [chatbotTermsUpToDate, setChatbotTermsUpToDate] = useState(sharedStatus)
  const [hasLoadedTermsStatus, setHasLoadedTermsStatus] = useState(sharedHasLoadedTermsStatus)
  const [revision, setRevision] = useState<string | undefined>(sharedRevision)

  useEffect(() => {
    statusListeners.add(setChatbotTermsUpToDate)
    hasLoadedListeners.add(setHasLoadedTermsStatus)
    revisionListeners.add(setRevision)

    return () => {
      statusListeners.delete(setChatbotTermsUpToDate)
      hasLoadedListeners.delete(setHasLoadedTermsStatus)
      revisionListeners.delete(setRevision)
    }
  }, [])

  useEffect(() => {
    (async () => {
      if (sharedHasLoadedTermsStatus) return

      try {
        const response = await Api.Chatbot.getTermsStatus()
        setSharedStatus(response.chatbotTermsUpToDate)
      } catch {
        setSharedStatus(false)
      } finally {
        setSharedHasLoadedTermsStatus(true)
      }
    })()
  }, [])

  useEffect(() => {
    (async () => {
      if (sharedRevision) return

      try {
        const response = await Api.Chatbot.getLatestRevision()
        setSharedRevision(response.revision)
      } catch {
        setSharedRevision(undefined)
      }
    })()
  }, [])

  const refreshChatbotTermsStatus = () => {
    setSharedStatus(true)
    setSharedHasLoadedTermsStatus(true)
  }

  return {
    chatbotTermsUpToDate,
    hasLoadedTermsStatus,
    refreshChatbotTermsStatus,
    revision
  }
}

export default useChatbotTermsApi
